import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  Controls,
  Handle,
  MiniMap,
  Position,
  ReactFlow,
  useReactFlow,
} from '@xyflow/react'

function uniqSorted(list) {
  return Array.from(new Set(list.filter(Boolean))).sort((a, b) => String(a).localeCompare(String(b)))
}

function getEdgePort(edge, key, fallbackKey) {
  return edge?.[key] ?? edge?.[fallbackKey] ?? null
}

const DND_NODE_TYPE = 'application/x-kquant-node'

export default function FlowCanvas({ nodes, edges, setNodes, setEdges, nodeDefs, onCreateNode, canvasElRef, onInit }) {
  const { screenToFlowPosition } = useReactFlow()
  const selectedNodeIds = useMemo(() => (nodes || []).filter((n) => n?.selected).map((n) => n.id), [nodes])
  const selectedEdgeIds = useMemo(() => (edges || []).filter((e) => e?.selected).map((e) => e.id), [edges])

  const [contextMenu, setContextMenu] = useState(null)

  const onNodesChange = useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [setNodes],
  )

  const onEdgesChange = useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [setEdges],
  )

  const deleteSelected = useCallback(() => {
    if (selectedNodeIds.length === 0 && selectedEdgeIds.length === 0) return

    const removedNodeIds = new Set(selectedNodeIds)
    const removedEdgeIds = new Set(selectedEdgeIds)

    setNodes((nds) => (nds || []).filter((n) => !removedNodeIds.has(n.id)))
    setEdges((eds) =>
      (eds || []).filter(
        (e) => !removedEdgeIds.has(e.id) && !removedNodeIds.has(e.source) && !removedNodeIds.has(e.target),
      ),
    )
  }, [selectedEdgeIds, selectedNodeIds, setEdges, setNodes])

  const deleteNodeById = useCallback(
    (nodeId) => {
      if (!nodeId) return
      setNodes((nds) => (nds || []).filter((n) => n.id !== nodeId))
      setEdges((eds) => (eds || []).filter((e) => e.source !== nodeId && e.target !== nodeId))
    },
    [setEdges, setNodes],
  )

  const deleteEdgeById = useCallback(
    (edgeId) => {
      if (!edgeId) return
      setEdges((eds) => (eds || []).filter((e) => e.id !== edgeId))
    },
    [setEdges],
  )

  const openContextMenu = useCallback((e, payload) => {
    e.preventDefault()
    e.stopPropagation()

    const MENU_W = 190
    const MENU_H = 44
    const padding = 8

    const maxX = Math.max(padding, window.innerWidth - MENU_W - padding)
    const maxY = Math.max(padding, window.innerHeight - MENU_H - padding)
    const x = Math.min(Math.max(padding, e.clientX), maxX)
    const y = Math.min(Math.max(padding, e.clientY), maxY)

    setContextMenu({ x, y, ...payload })
  }, [])

  useEffect(() => {
    function isTextInput(el) {
      if (!el) return false
      const tag = String(el.tagName || '').toLowerCase()
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return true
      return Boolean(el.isContentEditable)
    }

    function onKeyDown(e) {
      if (e.key !== 'Backspace' && e.key !== 'Delete') return
      if (isTextInput(e.target)) return
      if (selectedNodeIds.length === 0 && selectedEdgeIds.length === 0) return
      e.preventDefault()
      deleteSelected()
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [deleteSelected, selectedEdgeIds, selectedNodeIds])

  const nodeTypes = useMemo(() => {
    const inById = new Map()
    const outById = new Map()

    for (const e of edges || []) {
      const src = e?.source
      const tgt = e?.target
      if (!src || !tgt) continue

      const srcPort = getEdgePort(e, 'sourceHandle', 'source_port')
      const tgtPort = getEdgePort(e, 'targetHandle', 'target_port')

      if (!outById.has(src)) outById.set(src, [])
      if (!inById.has(tgt)) inById.set(tgt, [])

      outById.get(src).push(srcPort || 'out')
      inById.get(tgt).push(tgtPort || 'in')
    }

    const defs = Array.isArray(nodeDefs) ? nodeDefs : []
    const defByName = new Map(defs.map((d) => [d.name, d]))

    function Node({ id, data }) {
      const nodeType = data?.type || data?.label || 'node'
      const def = defByName.get(nodeType)

      const title = data?.label || nodeType
      const sub = def?.description || data?.subtitle

      const outputName = data?.output_name || data?.params?.output_name || null

      const inPorts = uniqSorted(inById.get(id) || [])
      const outPorts = uniqSorted(outById.get(id) || [])

      const finalIn = inPorts.length ? inPorts : ['in']
      const finalOut = outPorts.length ? outPorts : ['out']

      return (
        <div
          style={{
            padding: 10,
            borderRadius: 12,
            border: '1px solid var(--border-subtle)',
            background: 'var(--bg-panel)',
            minWidth: 200,
            color: 'var(--text-primary)',
            boxShadow: '0 10px 22px rgba(0,0,0,0.35)',
          }}
        >
          {finalIn.slice(0, 16).map((p, idx) => (
            <Handle
              key={`in_${p}`}
              id={p}
              type="target"
              position={Position.Left}
              className="rfHandle"
              style={{ top: 46 + idx * 16 }}
            />
          ))}
          {finalOut.slice(0, 16).map((p, idx) => (
            <Handle
              key={`out_${p}`}
              id={p}
              type="source"
              position={Position.Right}
              className="rfHandle"
              style={{ top: 46 + idx * 16 }}
            />
          ))}

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: 10,
              borderBottom: '1px solid var(--border-subtle)',
              paddingBottom: 6,
              marginBottom: 6,
            }}
          >
            <div style={{ fontWeight: 650, fontSize: 13 }}>{title}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {outputName ? (
                <span
                  className="badge"
                  style={{ fontSize: 10, padding: '2px 6px', borderRadius: 999, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}
                  title="output_name"
                >
                  {String(outputName)}
                </span>
              ) : null}
              {def?.category ? (
                <span className="badge badge-blue" style={{ fontSize: 10, padding: '2px 6px', borderRadius: 999 }}>
                  {def.category}
                </span>
              ) : null}
            </div>
          </div>
          {sub ? <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{sub}</div> : null}
        </div>
      )
    }

    return { default: Node }
  }, [edges, nodeDefs])

  const onConnect = useCallback((params) => setEdges((eds) => addEdge(params, eds)), [setEdges])

  const onDragOver = useCallback((e) => {
    if (!e?.dataTransfer) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }, [])

  const onDrop = useCallback(
    (e) => {
      const nodeType = e?.dataTransfer?.getData?.(DND_NODE_TYPE)
      if (!nodeType) return

      e.preventDefault()
      e.stopPropagation()

      const position = screenToFlowPosition({ x: e.clientX, y: e.clientY })
      onCreateNode?.(nodeType, position)
    },
    [onCreateNode, screenToFlowPosition],
  )

  return (
    <div style={{ position: 'absolute', inset: 0 }} ref={canvasElRef} onDragOver={onDragOver} onDrop={onDrop}>
      <ReactFlow
        colorMode="dark"
        nodes={nodes}
        edges={edges}
        onInit={onInit}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onPaneClick={() => setContextMenu(null)}
        onNodeContextMenu={(e, node) => openContextMenu(e, { kind: 'node', id: node?.id })}
        onEdgeContextMenu={(e, edge) => openContextMenu(e, { kind: 'edge', id: edge?.id })}
        nodeTypes={nodeTypes}
        fitView
        minZoom={0.1}
        proOptions={{ hideAttribution: true }}
        deleteKeyCode={null}
        defaultEdgeOptions={{
          type: 'default',
          style: { strokeWidth: 2 },
          animated: false,
        }}
        connectionLineStyle={{ stroke: 'var(--accent-blue)', strokeWidth: 2 }}
      >
        <MiniMap 
            pannable 
            zoomable 
            style={{ background: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }} 
            nodeColor={() => 'var(--accent-blue)'}
            maskColor="rgba(13, 17, 23, 0.6)"
        />
        <Controls />
        <Background color="var(--border-subtle)" gap={16} size={1} />
      </ReactFlow>

       {contextMenu ? (
         <div
           role="presentation"
           onMouseDown={() => setContextMenu(null)}
           style={{ position: 'fixed', inset: 0, zIndex: 50 }}
         >
          <div
            role="menu"
            style={{
              position: 'fixed',
              left: contextMenu.x,
              top: contextMenu.y,
              width: 190,
              borderRadius: 12,
              border: '1px solid var(--border-subtle)',
              background: 'var(--bg-panel)',
              boxShadow: '0 18px 50px rgba(0,0,0,0.55)',
              overflow: 'hidden',
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-[var(--bg-panel-hover)]"
              onClick={() => {
                if (contextMenu.kind === 'node') deleteNodeById(contextMenu.id)
                if (contextMenu.kind === 'edge') deleteEdgeById(contextMenu.id)
                setContextMenu(null)
              }}
            >
              <span>Delete</span>
              <span className="text-xs text-[var(--text-secondary)]">Del</span>
            </button>
          </div>
        </div>
       ) : null}
     </div>
   )
 }
